import type { WriteState } from "../../hooks/useWrite";
import { Notice } from "../ui";

/**
 * Whatever went wrong with a write, said out loud.
 *
 * WHY THIS EXISTS. Every write control branched on the handful of states its
 * author happened to think of — `BlockControl` and `CapEditor` on two,
 * `AppAccessControl` on four, out of `useWrite`'s fifteen. The states nobody
 * listed rendered NOTHING: a 500, a 503, an expired session or a throttled
 * request left the dialog open with no message, so the operator pressed the
 * button and nothing happened. Reported from dev on the AI-apps allowlist,
 * and the same gap sat on the merchant blocklist, where a silently failed
 * block during an incident is the worst version of it.
 *
 * One component, so the failure path is written once and a control cannot
 * quietly omit it. `useWrite` gaining a state means changing this file, not
 * auditing every caller.
 *
 * SUCCESS IS NOT HERE. `done` has a `WriteResult` with the effect timing and
 * per-control wording; mixing the two would make this the component every
 * write renders twice.
 *
 * Returns null for the three non-failures (`idle`, `submitting`, `done`), so
 * a caller can render it unconditionally.
 */
export function WriteFailure({ state }: { state: WriteState }) {
  switch (state.kind) {
    case "idle":
    case "submitting":
    case "done":
      return null;

    case "signedout":
      return (
        <Failure title="Not written">Your session ended. Sign in again and retry.</Failure>
      );

    case "idleReauth":
      // Distinct from `signedout`: the operator IS signed in, the write was
      // simply not trusted after an idle gap. Telling them their session
      // ended sends them looking for a sign-in page already on its way.
      return (
        <Failure tone="amber" title="Nothing was written">
          This session has been idle, so the change was not sent. Confirming your identity
          now — retry once you are back.
        </Failure>
      );

    case "rateLimited":
      // The gateway throttling, not a product. There is no Retry-After to
      // quote, so this does not invent a number.
      return (
        <Failure tone="amber" title="Too many changes just now">
          The gateway is throttling requests. Nothing was written — wait a moment and retry.
        </Failure>
      );

    case "versionConflict":
      return (
        <Failure tone="amber" title="Somebody changed this first">
          Nothing was written — this was changed by someone else
          {state.currentVersion !== undefined ? ` (now version ${state.currentVersion})` : ""}.
          Re-read it and decide again.
        </Failure>
      );

    case "alreadyInState":
      // NOT a failure to the operator: the resource already holds what they
      // asked for. Saying "refused" sends them hunting a problem that does
      // not exist.
      return <Failure title="Already the case">Nothing needed changing.</Failure>;

    case "idempotencyConflict":
      return (
        <Failure tone="amber" title="Not written">
          A request key was reused for a different change. Retry; if this repeats it is a bug
          in this console, not in your change.
        </Failure>
      );

    case "guardRefused":
      // The body names what the refusal would break — the entire point of
      // the guard, so it is surfaced verbatim.
      return <Failure title="Refused by a guard">{state.error.message}</Failure>;

    case "invalid":
      return <Failure title="Refused">{state.error.message}</Failure>;

    case "denied":
      return <Failure title="Not allowed">{state.error.message}</Failure>;

    case "notfound":
      return <Failure title="Not found">{state.error.message}</Failure>;

    case "unavailable":
      return <Failure title="Could not reach it">{state.error.message}</Failure>;

    case "error":
      return <Failure title="Not written">{state.error.message}</Failure>;
  }
}

function Failure({
  title,
  tone = "red",
  children,
}: {
  title: string;
  tone?: "red" | "amber";
  children: React.ReactNode;
}) {
  return (
    <div className="mt-2">
      <Notice tone={tone} title={title}>
        {children}
      </Notice>
    </div>
  );
}
